import { useEffect } from "react";
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from "framer-motion";
import styled from "@emotion/styled";

import { paper, fonts, easing } from "../theme";
import { getHostName, getPlayerName } from "../players";
import { useWebSocket } from "../useWebSocket";
import * as haptics from "../haptics";
import type { PlayerInfo, VotingResultsView } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsDisplay, MsEyebrow, MsRuleRed,
    MsBtn, MsStamp, MsWaitingFor,
    MsPaper, MsTape, MsQuoteWrap, MsBigQuote,
    MaskHeadline, Confetti,
    Stack, Row,
} from "../ms";

const CenteredBody = styled(MsBody)`
    justify-content: center;
    gap: 14px;
`;

const NoWinnerStack = styled(Stack)`
    text-align: center;
`;

const NoWinnerDisplay = styled(MsDisplay)`
    font-size: 30px;
`;

/* Keeps the label and the card on one column so the eyebrow lines up with the
 * card edge, the way Results puts "Entry 2 of 5" above its card. Takes the full
 * content column, same as a revealed entry — no narrower hero width, or the
 * winning entry would arrive smaller than it was when it was read out. */
const WinnerBlock = styled.div`
    width: 100%;
`;

const HeadRow = styled(Row)`
    margin-bottom: 10px;
    padding-left: 2px;
`;

/* Carries the `winning-quote` layoutId and nothing else — framer owns its
 * transform for the morph out of the voting grid and on into the leaderboard, so
 * the tilt and the drop live on the paper inside. */
const WinnerCard = styled(motion.div)`
    width: 100%;
`;

/* Same clipping as a revealed entry: deckle edge, one tape strip, a hair of
 * rotation. The old treatment nested a black photo panel inside a white polaroid
 * frame, which read as a different app to every other screen. */
const WinnerPaper = styled(MsPaper)`
    padding: 30px 22px 26px;
`;

const WinnerQuote = styled(MsDisplay)`
    font-size: 30px;
    font-weight: 700;
    position: relative;
`;

/* Draws itself out from the left once the entry has landed — the beat between
 * the quote arriving and the score being counted. */
const WinnerRule = styled(MsRuleRed)`
    margin-top: 18px;
    animation: ms-bar-grow 0.45s ${easing.standard} 0.5s both;
`;

const MetaRow = styled(Row)`
    margin-top: 10px;
`;

const WinnerNames = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 700;
    font-size: 18px;
    color: ${paper.inkSoft};
    min-width: 0;
`;

/* The score is typography, not a badge — the meta row already reads as a byline,
 * so the payoff is simply the same voice in red, one size up. */
const ScoreWrap = styled(motion.div)`
    display: flex;
    align-items: baseline;
    gap: 4px;
    flex-shrink: 0;
    color: ${paper.red};
`;

const ScoreNum = styled(motion.div)`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 800;
    font-size: 26px;
    line-height: 1;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
`;

const ScoreUnit = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 700;
    font-size: 15px;
`;

interface Props {
    view: VotingResultsView;
    players: PlayerInfo[];
    self: PlayerInfo;
}

/** Counts the score up so the number lands rather than just appearing. */
function Score({ value }: { value: number }) {
    const reduced = useReducedMotion();
    const count = useMotionValue(reduced ? value : 0);
    const shown = useTransform(count, v => `+${Math.round(v)}`);

    useEffect(() => {
        if (reduced) {
            count.set(value);
            return;
        }
        const controls = animate(count, value, {
            duration: 0.7,
            delay: 0.6,
            ease: [0.22, 1, 0.36, 1],
        });
        return () => controls.stop();
    }, [value, reduced, count]);

    return (
        <ScoreWrap
            initial={reduced ? undefined : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
            <ScoreNum>{shown}</ScoreNum>
            <ScoreUnit>{value === 1 ? "pt" : "pts"}</ScoreUnit>
        </ScoreWrap>
    );
}

export default function VotingResults({ view, players, self }: Props) {
    const { sendMessage } = useWebSocket();
    const { results, answers } = view;

    const tallies = Object.entries(results).map(([playerId, votes]) => ({
        playerId,
        playerName: getPlayerName(playerId, players),
        votes,
        answer: answers[playerId],
    }));
    const maxVotes = tallies.length > 0 ? Math.max(...tallies.map(t => t.votes)) : 0;
    const winners = tallies.filter(t => t.votes === maxVotes && maxVotes > 0);

    /* Pairs with the confetti below — the burst is the only haptic that marks a
     * result rather than an action, so it fires on the same condition. */
    useEffect(() => {
        if (winners.length > 0) haptics.celebrate();
    }, [winners.length]);

    const next = () => sendMessage({ type: "advance_results" });

    return (
        <MsScreen>
            {winners.length > 0 && <Confetti />}
            <CenteredBody>
                {winners.length === 0 ? (
                    <NoWinnerStack gap={14} align="center">
                        <MsStamp>No winner</MsStamp>
                        <NoWinnerDisplay>
                            <MaskHeadline>Nobody won this round.</MaskHeadline>
                        </NoWinnerDisplay>
                    </NoWinnerStack>
                ) : (
                    <WinnerBlock>
                        <HeadRow justify="space-between" align="baseline">
                            <MsEyebrow tone="red">
                                {winners.length === 1 ? "Winning entry" : "Tied for the win"}
                            </MsEyebrow>
                            <MsEyebrow tone="mute">
                                {maxVotes} {maxVotes === 1 ? "vote" : "votes"}
                            </MsEyebrow>
                        </HeadRow>
                        <WinnerCard layoutId="winning-quote">
                            <WinnerPaper deckle drop r0="4deg" r1="-1deg">
                                <MsTape placement="tl" />
                                <MsQuoteWrap>
                                    <MsBigQuote>"</MsBigQuote>
                                    <WinnerQuote>
                                        <MaskHeadline delay={0.2}>
                                            {winners[0].answer || "—"}
                                        </MaskHeadline>
                                    </WinnerQuote>
                                </MsQuoteWrap>
                                <WinnerRule />
                                <MetaRow justify="space-between" align="baseline">
                                    <WinnerNames>
                                        — {winners.map(w => w.playerName.toLowerCase()).join(" & ")}
                                    </WinnerNames>
                                    <Score value={maxVotes} />
                                </MetaRow>
                            </WinnerPaper>
                        </WinnerCard>
                    </WinnerBlock>
                )}
            </CenteredBody>
            <MsFooter>
                {self?.isHost ? (
                    <MsBtn variant="red" onClick={next}>Standings</MsBtn>
                ) : (
                    <MsWaitingFor name={getHostName(players)} />
                )}
            </MsFooter>
        </MsScreen>
    );
}
